<template>
  <div class="picture-card">
    <div class="card h-100">
      <!-- Image thumbnail -->
      <div class="card-img-container position-relative">
        <img 
          :src="picture.thumbnailUrl" 
          :alt="picture.description"
          class="card-img-top"
          @load="onImageLoad"
          @error="onImageError"
          :class="{ 'd-none': !imageLoaded }"
        >
        
        <!-- Loading placeholder -->
        <div 
          v-if="!imageLoaded && !imageError" 
          class="image-placeholder d-flex align-items-center justify-content-center"
        >
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Načítám...</span>
          </div>
        </div>
        
        <!-- Error placeholder -->
        <div 
          v-if="imageError" 
          class="image-placeholder d-flex align-items-center justify-content-center bg-light"
        >
          <i class="bi bi-image-alt text-muted fs-1"></i>
        </div>
        
        <!-- Delete button -->
        <button
          @click="handleDelete"
          class="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
          type="button"
          title="Smazat obrázek"
        >
          <i class="bi bi-trash"></i>
        </button>
        
        <!-- View full image button -->
        <button
          @click="viewFullImage"
          class="btn btn-light btn-sm position-absolute bottom-0 end-0 m-2"
          type="button"
          title="Zobrazit v plné velikosti"
        >
          <i class="bi bi-arrows-fullscreen"></i>
        </button>
      </div>
      
      <!-- Card body -->
      <div class="card-body d-flex flex-column">
        <p class="card-text flex-grow-1">{{ picture.description }}</p>
        
        <div class="card-footer-info">
          <small class="text-muted d-block">
            <i class="bi bi-file-earmark"></i>
            {{ picture.filename }}
          </small>
          <small class="text-muted d-block">
            <i class="bi bi-hdd"></i>
            {{ formatFileSize(picture.size) }}
          </small>
          <small class="text-muted d-block">
            <i class="bi bi-calendar"></i>
            {{ formatDate(picture.uploadedAt) }}
          </small>
        </div>
      </div>
    </div>
    
    <!-- Full image modal -->
    <div 
      v-if="showModal" 
      class="modal fade show d-block" 
      tabindex="-1" 
      style="background-color: rgba(0,0,0,0.5);"
      @click="closeModal"
    >
      <div class="modal-dialog modal-fullscreen modal-dialog-centered">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">{{ picture.description }}</h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="closeModal"
            ></button>
          </div>
          <div class="modal-body text-center">
            <img 
              :src="picture.fullImageUrl" 
              :alt="picture.description"
              class="img-fluid"
              style="max-height: 70vh;"
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue'

const emit = defineEmits(['delete'])

const props = defineProps({
  picture: {
    type: Object,
    required: true
  }
})

const imageLoaded = ref(false)
const imageError = ref(false)
const showModal = ref(false)

const onImageLoad = () => {
  imageLoaded.value = true
  imageError.value = false
}

const onImageError = () => {
  imageLoaded.value = false
  imageError.value = true
}

const handleDelete = () => {
  if (confirm('Opravdu chcete smazat tento obrázek?')) {
    emit('delete', props.picture.id)
  }
}

const viewFullImage = () => {
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
}

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B'
  
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

const formatDate = (date) => {
  if (!date) return ''
  
  return new Intl.DateTimeFormat('cs-CZ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(date))
}
</script>

<style scoped>
.picture-card .card {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.picture-card .card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.card-img-container {
  height: 200px;
  overflow: hidden;
}

.card-img-top {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease-in-out;
}

.image-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #f8f9fa;
}

.card-footer-info {
  border-top: 1px solid #dee2e6;
  padding-top: 0.5rem;
  margin-top: 0.5rem;
}

.card-footer-info small {
  font-size: 0.75rem;
  margin-bottom: 0.25rem;
}

.card-footer-info i {
  margin-right: 0.25rem;
  width: 12px;
}

/* Modal styling */
.modal.show {
  display: block !important;
}
</style>